import { useState, useCallback } from 'react';
import { useTracking } from 'react-tracking';

import useUser from '@/hooks/useUser';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';
import { NarrativeFairValueModal } from '@/components/NarrativeFairValue';
import GuestRegistrationModal from '@components/GuestRegistrationModal';

import { FeedCardMyFairValueUnset } from './FeedCardMyFairValueUnset';
import { FeedCardMyFairValueSelected } from './FeedCardMyFairValueSelected';
import type { FragmentOf } from 'gql.tada';
import type { FEED_CARD_COMPANY_FRAGMENT } from './feedCardCompanyFragment';
import { NarrativePanelRegister } from '@/components/GuestRegistrationModal/components/NarrativePanelRegister';

type Props = {
  company: FragmentOf<typeof FEED_CARD_COMPANY_FRAGMENT>;
};

export const FeedCardMyFairValue = ({ company }: Props) => {
  const { trackEvent } = useTracking({ subject: 'primary_narrative' });
  const { data: user } = useUser();
  const [isNarrativeFairValueModalOpen, setNarrativeFairValueModal] =
    useState(false);
  const [isGuestRegistrationModalOpen, setGuestRegistrationModal] =
    useState(false);

  const onClick = useCallback(() => {
    if (user.loggedIn) {
      setNarrativeFairValueModal(true);
    } else {
      setGuestRegistrationModal(true);
    }
  }, [user.loggedIn]);

  const { id: companyId, myValuation } = company;
  const currencyISO = getTradingItemCurrencyISO(company.data.currencyInfo);

  return (
    <>
      {!myValuation ? (
        <FeedCardMyFairValueUnset
          onClick={() => {
            trackEvent({ action: 'select' });
            onClick();
          }}
        />
      ) : (
        <FeedCardMyFairValueSelected
          currencyISO={currencyISO}
          fairValue={myValuation?.fairValue ?? null}
          onClick={() => {
            trackEvent({ action: 'edit' });
            onClick();
          }}
        />
      )}
      <div
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <NarrativeFairValueModal
          isModalOpen={isNarrativeFairValueModalOpen}
          onClose={() => setNarrativeFairValueModal(false)}
          companyId={companyId}
        />
        {isGuestRegistrationModalOpen && (
          <GuestRegistrationModal
            canClose
            onClose={() => setGuestRegistrationModal(false)}
            removeWindowScroll
            onLoginSuccess={() => setGuestRegistrationModal(false)}
            onRegisterSuccess={() => setGuestRegistrationModal(false)}
            SplashPanel={{
              Component: NarrativePanelRegister,
              isDark: true,
            }}
          />
        )}
      </div>
    </>
  );
};
